/**
 * Guardia sulle modifiche non salvate, per la navigazione **dentro** l'app.
 *
 * Il ProgramBuilder avvisava già in due casi: chiusura della scheda
 * (`beforeunload`) e il proprio bottone «indietro». Ma un click su una voce
 * del menu laterale — «Libreria drill», mentre si sta scrivendo un programma
 * — è una navigazione di React Router, che non passa da nessuno dei due: si
 * usciva senza una parola e il lavoro spariva. È quello che è successo al
 * test.
 *
 * **Come funziona.** Chi ha uno stato da proteggere si registra con una
 * funzione «sono sporco?» (`useUnsavedChangesGuard`). Il provider ascolta i
 * click sui link interni **in fase di cattura**, prima che React Router li
 * veda: se qualcuno è sporco chiede conferma, e se la risposta è no ferma
 * l'evento — `Link` controlla `defaultPrevented` prima di navigare, quindi
 * basta questo. Un solo punto, nessun `onClick` da aggiungere a decine di
 * `<Link>`.
 *
 * Non copre il tasto indietro del browser (richiederebbe il data router) né
 * le `navigate()` programmatiche, che restano a carico di chi le chiama.
 *
 * Spec: docs/shooting/15-seconda-sessione-federico.md
 */
import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useId,
	useRef,
} from "react";
import { useLanguage } from "./LanguageContext";

const UnsavedChangesContext = createContext(null);

const isInternalNavigation = (anchor) => {
	if (!anchor) return false;
	if (anchor.target && anchor.target !== "_self") return false;
	if (anchor.hasAttribute("download")) return false;
	const href = anchor.getAttribute("href") || "";
	if (!href || href.startsWith("#") || href.startsWith("//")) return false;
	if (!href.startsWith("/")) return false;
	const current = `${window.location.pathname}${window.location.search}`;
	return href !== current && href !== window.location.pathname;
};

export function UnsavedChangesProvider({ children }) {
	const { t } = useLanguage();
	const guards = useRef(new Map());
	const translate = useRef(t);
	useEffect(() => {
		translate.current = t;
	}, [t]);

	const register = useCallback((id, isDirty) => {
		guards.current.set(id, isDirty);
		return () => {
			guards.current.delete(id);
		};
	}, []);

	const hasUnsaved = useCallback(() => {
		for (const isDirty of guards.current.values()) {
			try {
				if (isDirty()) return true;
			} catch {
				// Una guardia che esplode non deve bloccare la navigazione.
			}
		}
		return false;
	}, []);

	const confirmLeave = useCallback(
		() => !hasUnsaved() || window.confirm(translate.current("pbUnsavedLeaveConfirm")),
		[hasUnsaved],
	);

	useEffect(() => {
		const onClick = (event) => {
			if (event.defaultPrevented || event.button !== 0) return;
			if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
			const anchor = event.target?.closest?.("a[href]");
			if (!isInternalNavigation(anchor)) return;
			if (!hasUnsaved()) return;
			if (window.confirm(translate.current("pbUnsavedLeaveConfirm"))) return;
			event.preventDefault();
			event.stopPropagation();
		};
		// Cattura: deve girare prima dell'`onClick` di `<Link>`.
		document.addEventListener("click", onClick, true);
		return () => document.removeEventListener("click", onClick, true);
	}, [hasUnsaved]);

	return (
		<UnsavedChangesContext.Provider value={{ register, hasUnsaved, confirmLeave }}>
			{children}
		</UnsavedChangesContext.Provider>
	);
}

/**
 * Registra uno stato da proteggere. `isDirty` è un booleano: lo si passa a
 * ogni render e qui si legge l'ultimo valore quando serve.
 *
 * Senza provider (test, pagine fuori dall'app) non fa niente.
 */
export function useUnsavedChangesGuard(isDirty) {
	const context = useContext(UnsavedChangesContext);
	const id = useId();
	const dirtyRef = useRef(isDirty);
	useEffect(() => {
		dirtyRef.current = isDirty;
	}, [isDirty]);
	useEffect(() => {
		if (!context) return undefined;
		return context.register(id, () => dirtyRef.current);
	}, [context, id]);
}

export function useUnsavedChanges() {
	return useContext(UnsavedChangesContext);
}
